import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Heart, ShieldCheck, Plane } from "lucide-react";
import { MaskedLines, Reveal } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { PuppyCard } from "../components/PuppyCard";
import { getPuppies } from "../lib/api";

const HERO_IMG =
  "https://images.unsplash.com/photo-1598411466690-9d38a1dfdc67?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwzfHxjdXRlJTIwcHVwcHklMjBpbiUyMHRlYWN1cHxlbnwwfHx8fDE3ODQ4ODE0OTZ8MA&ixlib=rb-4.1.0&q=85";
const KIDS_IMG =
  "https://images.pexels.com/photos/10418368/pexels-photo-10418368.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const CHAPTERS = [
  { n: "01", title: "Ethically Raised", body: "Every puppy is born and nurtured inside our home — never a kennel. They wake to gentle hands and fall asleep to lullabies." },
  { n: "02", title: "Health Guaranteed", body: "Full veterinary clearance, age-appropriate vaccinations, and a written health guarantee accompany each and every puppy." },
  { n: "03", title: "Delivered With Love", body: "From white-glove USA delivery to international flight nanny service, your puppy arrives calm, safe, and adored." },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.35, 0.7]);

  useEffect(() => {
    getPuppies("available").then((d) => setFeatured(d.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative h-screen min-h-[680px] w-full overflow-hidden">
        <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0">
          <img src={HERO_IMG} alt="Teacup puppy in a cup" className="h-full w-full object-cover" />
        </motion.div>
        <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-[#2A2118]" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#2A2118]/70 via-transparent to-[#2A2118]/20" />

        <div className="relative z-10 h-full max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col justify-end pb-24">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.15, duration: 1 }}
            className="label-eyebrow text-[#E6C5C0] mb-6"
          >
            Boutique Teacup Puppies &middot; Worldwide Delivery
          </motion.p>
          <h1 className="font-heading text-[#FDFBF7] text-5xl md:text-7xl lg:text-8xl font-light leading-[0.95] tracking-tight max-w-4xl">
            <MaskedLines lines={["The world's tiniest", "companions, raised", "as family."]} />
          </h1>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.9 }}
            className="flex flex-wrap items-center gap-4 mt-10"
          >
            <Link
              to="/puppies"
              data-testid="hero-shop-btn"
              className="group inline-flex items-center gap-2 bg-[#FDFBF7] text-[#2A2118] px-8 py-4 rounded-sm text-sm tracking-wide hover:bg-[#E6C5C0] transition-colors duration-300"
            >
              Meet the Puppies
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/care"
              data-testid="hero-story-btn"
              className="inline-flex items-center gap-2 border border-[#FDFBF7]/50 text-[#FDFBF7] px-8 py-4 rounded-sm text-sm tracking-wide hover:bg-[#FDFBF7]/10 transition-colors duration-300"
            >
              Our Story
            </Link>
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-[#E2D1C3] bg-[#F5E6E3]/40">
        <Marquee items={["Tiny", "Healthy", "Adored", "Hand-Raised", "Worldwide", "Forever Homes"]} />
      </section>

      {/* INTRO */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-28">
        <div className="grid lg:grid-cols-12 gap-12 items-end">
          <Reveal className="lg:col-span-7">
            <p className="label-eyebrow text-[#C8A98E] mb-6">The Boutique</p>
            <h2 className="font-heading text-4xl md:text-6xl font-light leading-[1.05] text-[#2A2118]">
              A rare standard of care for extraordinarily small dogs.
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5">
            <p className="text-[#5c5245] text-lg font-light leading-relaxed">
              We are a small, family-run boutique devoted entirely to teacup breeds. Each puppy is
              hand-raised in our home, socialized with our children, and matched thoughtfully with a
              loving forever family. This is not a kennel — it is a nursery.
            </p>
          </Reveal>
        </div>
      </section>

      {/* FEATURED PUPPIES */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-28">
        <div className="flex items-end justify-between mb-12">
          <Reveal>
            <p className="label-eyebrow text-[#C8A98E] mb-3">Now Available</p>
            <h2 className="font-heading text-4xl md:text-5xl font-light text-[#2A2118]">Meet our little ones</h2>
          </Reveal>
          <Link to="/puppies" data-testid="view-all-puppies" className="hidden md:inline-flex items-center gap-2 text-sm text-[#2A2118] hover:text-[#C8A98E] transition-colors">
            View all <ArrowRight size={16} />
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {featured.map((p, i) => (
            <PuppyCard key={p.id} puppy={p} index={i} />
          ))}
        </div>
      </section>

      {/* MANIFESTO CHAPTERS */}
      <section className="bg-[#2A2118] text-[#FDFBF7] py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal>
            <p className="label-eyebrow text-[#E6C5C0] mb-6">Our Promise</p>
            <h2 className="font-heading text-4xl md:text-6xl font-light max-w-2xl leading-tight mb-16">
              Three principles we will never compromise.
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-12">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.12}>
                <div className="border-t border-white/15 pt-6">
                  <span className="font-heading text-6xl font-light text-[#C8A98E]">{c.n}</span>
                  <h3 className="font-heading text-3xl mt-4 mb-3 text-[#FDFBF7]">{c.title}</h3>
                  <p className="text-[#d8c4b8] font-light leading-relaxed">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAMILY / KIDS */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-28">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <div className="relative overflow-hidden rounded-md">
              <div className="absolute inset-0 spotlight z-10 pointer-events-none" />
              <img src={KIDS_IMG} alt="Child cuddling a puppy" className="w-full aspect-[4/3] object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="label-eyebrow text-[#C8A98E] mb-6">Part of the Family</p>
            <h2 className="font-heading text-4xl md:text-5xl font-light text-[#2A2118] leading-tight mb-6">
              Raised in the arms of our children.
            </h2>
            <p className="text-[#5c5245] text-lg font-light leading-relaxed mb-8">
              Our puppies grow up surrounded by cuddles, laughter, and gentle play. By the time they
              meet you, they are confident, affectionate, and beautifully socialized.
            </p>
            <div className="flex flex-wrap gap-6">
              {[{ icon: Heart, t: "Home-raised" }, { icon: ShieldCheck, t: "Health guarantee" }, { icon: Plane, t: "Flight nanny" }].map((f) => (
                <div key={f.t} className="flex items-center gap-2 text-[#2A2118]">
                  <f.icon size={18} className="text-[#C8A98E]" />
                  <span className="text-sm">{f.t}</span>
                </div>
              ))}
            </div>
            <Link to="/care" data-testid="home-care-link" className="inline-flex items-center gap-2 mt-10 text-sm text-[#2A2118] border-b border-[#C8A98E] pb-1 hover:text-[#C8A98E] transition-colors">
              Read our care philosophy <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-16">
        <div className="bg-[#F5E6E3] rounded-md px-8 md:px-20 py-20 text-center">
          <Reveal>
            <p className="label-eyebrow text-[#C8A98E] mb-6">Ready to welcome one home?</p>
            <h2 className="font-heading text-4xl md:text-6xl font-light text-[#2A2118] max-w-3xl mx-auto leading-tight">
              Your tiniest new family member is waiting.
            </h2>
            <div className="flex flex-wrap justify-center gap-4 mt-10">
              <Link to="/puppies" data-testid="cta-shop-btn" className="bg-[#2A2118] text-[#FDFBF7] px-8 py-4 rounded-sm text-sm hover:bg-[#3d3226] transition-colors">
                Browse Available Puppies
              </Link>
              <Link to="/contact" data-testid="cta-contact-btn" className="border border-[#2A2118] text-[#2A2118] px-8 py-4 rounded-sm text-sm hover:bg-[#2A2118] hover:text-[#FDFBF7] transition-colors">
                Contact Us
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
